import { makeAutoObservable, runInAction } from 'mobx';
import { wizardApi } from '@/services/wizard.api';
import type {
  AvailableOption,
  ConfigurationMetadata,
  MenuSubstep,
  OptionSaveResponse,
  PlanimetryDescriptor,
  SaveAnswerResponse,
  SubstepCopy,
  WizardPackage,
  WizardPosition,
} from '@/types';

export interface MenuPhaseGroup {
  phaseId: number;
  phaseKey: string;
  phaseName: string;
  phaseOrder: number;
  /** Raggruppamento per stanza. Le fasi non room-major hanno un unico gruppo con roomId=null. */
  rooms: MenuRoomGroup[];
}

export interface MenuRoomGroup {
  roomId: number | null;
  roomLabel: string | null;
  steps: MenuStepGroup[];
}

export interface MenuStepGroup {
  stepId: number;
  stepKey: string;
  stepName: string;
  stepOrder: number;
  substeps: MenuSubstep[];
}

/**
 * Store MobX unico del wizard. La navigazione (fasi/step/substep, ordine,
 * stato "risposto", quale substep viene dopo) è SEMPRE guidata dal backend:
 * - la struttura arriva da GET /configurations/:id/menu
 * - il "prossimo" arriva da GET /configurations/:id/next
 * Lo store NON calcola autonomamente alcuna sequenza: orchestra soltanto.
 */
class WizardStore {
  configurationId: number | null = null;

  /** Metadati di intestazione (nome config, tipologia + codice appartamento) per l'header. */
  metadata: ConfigurationMetadata | null = null;

  menu: MenuSubstep[] = [];
  isLoadingMenu = false;

  position: WizardPosition | null = null;

  packages: WizardPackage[] = [];
  isLoadingPackages = false;

  selectedPackageId: number | null = null;

  /** Opzioni del substep corrente (fasi a OPZIONE Layout/Tecnologia). Vuoto per i substep a pacchetto. */
  options: AvailableOption[] = [];
  /** Copy statica del substep corrente (tagline/caption/warning/note), dal BE. Null se non mappata. */
  substepCopy: SubstepCopy | null = null;
  isLoadingOptions = false;
  /** true durante il salvataggio di una modifica alle opzioni (toggle add-on / scelta-singola). */
  isSavingOptions = false;

  isSaving = false;
  isLoadingNext = false;

  /** Anteprima dryRun dell'ultima selezione: quante risposte successive verrebbero invalidate. */
  invalidationPreview: SaveAnswerResponse | null = null;

  /** true quando GET next ha restituito null: il wizard è completo. */
  isCompleted = false;

  /**
   * true all'ingresso in una configurazione (con layout): mostra la schermata START/intro
   * (elenco fasi + timeline) prima del wizard, come l'oracolo. `startConfiguration()` la chiude.
   */
  showIntro = false;

  /**
   * Valorizzato quando `proceed()` conclude una fase e la successiva è in un'altra fase: mostra la
   * schermata di transizione ("Passa alla fase successiva") invece di saltare dritto al substep.
   * `continueToNextPhase()` la chiude e naviga a `next`. Come l'oracolo (PhaseListPage).
   */
  phaseTransition: {
    completedPhaseKey: string;
    completedPhaseName: string;
    next: WizardPosition;
  } | null = null;

  /** Descrittore planimetria della configurazione (immagine composta = composeUrl). */
  planimetry: PlanimetryDescriptor | null = null;
  isLoadingPlanimetry = false;

  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  reset(): void {
    this.configurationId = null;
    this.metadata = null;
    this.menu = [];
    this.position = null;
    this.packages = [];
    this.selectedPackageId = null;
    this.options = [];
    this.substepCopy = null;
    this.invalidationPreview = null;
    this.isCompleted = false;
    this.showIntro = false;
    this.phaseTransition = null;
    this.planimetry = null;
    this.error = null;
  }

  /** Carica i metadati di intestazione della configurazione (header). Non blocca il wizard in errore. */
  async loadMetadata(configurationId: number): Promise<void> {
    try {
      const metadata = await wizardApi.getMetadata(configurationId);
      runInAction(() => {
        this.metadata = metadata;
      });
    } catch {
      // I metadati sono complementari: un errore non blocca il configuratore.
    }
  }

  async loadMenu(configurationId: number): Promise<void> {
    this.isLoadingMenu = true;
    this.error = null;
    try {
      const { substeps } = await wizardApi.getMenu(configurationId);
      runInAction(() => {
        this.configurationId = configurationId;
        this.menu = substeps;
        this.isLoadingMenu = false;
      });
      // La planimetria riflette lo stato risposte della config: caricata all'ingresso e
      // ricaricata dopo ogni salvataggio (confirmSelection ricarica il menu).
      void this.loadPlanimetry();
    } catch {
      runInAction(() => {
        this.error = 'Impossibile caricare il menu della configurazione. Riprova.';
        this.isLoadingMenu = false;
      });
    }
  }

  /**
   * Carica il descrittore di planimetria (immagine composta = composeUrl) della configurazione.
   * Con `roomId` valorizzato la vista include l'overlay stanza `R_<stanza>` (usato dalla CARTA PARATI
   * per evidenziare la parete della stanza corrente).
   */
  async loadPlanimetry(
    roomId: number | null = null,
    highlight: 'room' | null = null,
  ): Promise<void> {
    if (this.configurationId === null) return;
    this.isLoadingPlanimetry = true;
    try {
      const descriptor = await wizardApi.getPlanimetry(this.configurationId, 'plan', roomId, highlight);
      runInAction(() => {
        this.planimetry = descriptor;
        this.isLoadingPlanimetry = false;
      });
    } catch {
      // La planimetria è complementare: un suo errore non blocca il wizard.
      runInAction(() => {
        this.isLoadingPlanimetry = false;
      });
    }
  }

  /**
   * Entra nel wizard di una configurazione: carica il menu e determina il
   * punto di ripresa. L'UNICA euristica client-side ammessa è questa: il
   * primo substep con isAnswered=false nell'ordine (fase, step, substep) del
   * menu — analoga alla resume-logic del vecchio frontend, ma applicata
   * SOLO per decidere dove atterrare al primo ingresso. Ogni avanzamento
   * successivo passa sempre da GET /next (mai calcolato qui).
   *
   * GET /menu espone roomId/roomLabel per substep (i substep "room-major",
   * es. bagni multipli, sono ripetuti una volta per stanza), quindi questa
   * euristica atterra correttamente anche sulla stanza giusta.
   */
  async enterConfigurator(configurationId: number): Promise<void> {
    void this.loadMetadata(configurationId);
    await this.loadMenu(configurationId);
    if (this.menu.length === 0) return;

    // Ingresso: mostra la schermata START/intro (elenco fasi) prima del wizard, come l'oracolo.
    // La navigazione al primo substep avviene su START (`startConfiguration`).
    runInAction(() => {
      this.showIntro = true;
    });
  }

  /**
   * Chiude la schermata START/intro e naviga al primo substep non risposto (nell'ordine del menu).
   * Se tutto è già risposto marca `isCompleted` (l'intro offre "Vai al riepilogo").
   */
  async startConfiguration(): Promise<void> {
    runInAction(() => {
      this.showIntro = false;
    });
    const ordered = this.orderedMenu;
    const firstUnanswered = ordered.find((s) => !s.isAnswered);
    if (firstUnanswered === undefined) {
      runInAction(() => {
        this.isCompleted = true;
      });
      return;
    }
    await this.goToSubstep({
      substepId: firstUnanswered.substepId,
      roomId: firstUnanswered.roomId,
    });
  }

  /** Naviga direttamente a un substep cliccato nel menu (con la sua eventuale stanza). */
  async jumpToSubstep(substepId: number, roomId: number | null): Promise<void> {
    await this.goToSubstep({ substepId, roomId });
  }

  /**
   * Naviga verso un substep (click sul menu, o dopo GET next) e ne carica il contenuto instradando
   * su `inputType` del menu (deciso dal backend): 'options' → GET .../options, 'packages' →
   * GET .../packages. Nessun hardcode di fasi nel FE.
   */
  async goToSubstep(position: WizardPosition): Promise<void> {
    this.position = position;
    this.selectedPackageId = null;
    this.options = [];
    this.substepCopy = null;
    this.packages = [];
    this.invalidationPreview = null;
    this.isCompleted = false;
    this.showIntro = false;
    this.phaseTransition = null;
    if (this.currentInputType === 'options') {
      await this.loadOptions();
    } else {
      await this.loadPackages();
    }
    // Overlay stanza sulla planimetria a sinistra: solo la CARTA PARATI la mostra a sinistra con
    // l'overlay rivestimento (tag R_<stanza>). SCHEMA RIVESTIMENTO bagno è full-width (nessuna
    // planimetria nella vista card: nel legacy sta solo nella schermata intro `firstStep`).
    const key = this.currentMenuEntry?.substepKey;
    void this.loadPlanimetry(key === 'CARTAPARATI' ? position.roomId : null, null);
  }

  /** Carica le opzioni (fasi 1-2) del substep corrente. */
  async loadOptions(): Promise<void> {
    if (this.configurationId === null || this.position === null) return;
    this.isLoadingOptions = true;
    this.error = null;
    try {
      const { options, copy } = await wizardApi.getOptions(
        this.configurationId,
        this.position.substepId,
        this.position.roomId,
      );
      runInAction(() => {
        this.options = options;
        this.substepCopy = copy;
        this.isLoadingOptions = false;
      });
    } catch {
      runInAction(() => {
        this.error = 'Impossibile caricare le opzioni per questo passo. Riprova.';
        this.isLoadingOptions = false;
      });
    }
  }

  /**
   * Anteprima dryRun di una modifica alle opzioni: restituisce il piano (toDeleteIds) senza scrivere,
   * per mostrare l'InvalidationDialog quando la modifica invaliderebbe risposte a valle.
   */
  async previewOptions(optionIds: number[]): Promise<OptionSaveResponse | null> {
    if (this.configurationId === null || this.position === null || optionIds.length === 0) {
      return null;
    }
    const { substepId, roomId } = this.position;
    try {
      return await wizardApi.saveOptions(
        this.configurationId,
        { substepId, options: optionIds, ...(roomId != null ? { roomId } : {}) },
        true,
      );
    } catch {
      runInAction(() => {
        this.error = 'Impossibile verificare la modifica. Riprova.';
      });
      return null;
    }
  }

  /**
   * Salva una modifica alle opzioni (il nuovo insieme `options[]` SOSTITUISCE quello corrente del
   * substep), poi ricarica opzioni + menu + planimetria per riflettere stato/prezzi aggiornati.
   * Il salvataggio avviene a ogni toggle (comportamento del nuovo sistema: ogni answer fa POST).
   */
  async saveOptions(optionIds: number[]): Promise<void> {
    if (this.configurationId === null || this.position === null || optionIds.length === 0) return;
    const configurationId = this.configurationId;
    const { substepId, roomId } = this.position;
    this.isSavingOptions = true;
    this.error = null;
    try {
      await wizardApi.saveOptions(configurationId, {
        substepId,
        options: optionIds,
        ...(roomId != null ? { roomId } : {}),
      });
      // Ricarica le opzioni del substep (isAnswered/prezzi) e il menu (isAnswered + planimetria).
      await this.loadOptions();
      await this.loadMenu(configurationId);
    } catch {
      runInAction(() => {
        this.error = 'Impossibile salvare la modifica. Riprova.';
      });
    } finally {
      runInAction(() => {
        this.isSavingOptions = false;
      });
    }
  }

  /**
   * Prosegue al substep successivo SENZA salvare (le risposte a opzione sono già salvate a ogni
   * toggle; il ramo a pacchetto usa invece confirmSelection che salva e avanza). Chiede al backend
   * il prossimo substep (GET next) e vi si sposta; ricarica il menu per lo stato aggiornato.
   */
  async proceed(): Promise<void> {
    if (this.configurationId === null || this.position === null) return;
    const configurationId = this.configurationId;
    const currentSubstepId = this.position.substepId;
    const roomId = this.position.roomId;
    this.isLoadingNext = true;
    this.error = null;
    try {
      const { next } = await wizardApi.getNext(configurationId, currentSubstepId, roomId);
      runInAction(() => {
        this.isLoadingNext = false;
      });
      await this.loadMenu(configurationId);
      if (next === null) {
        runInAction(() => {
          this.isCompleted = true;
          this.position = null;
          this.options = [];
          this.packages = [];
        });
      } else {
        // Se il prossimo substep è in un'altra fase, mostra la transizione invece di saltarci dritto.
        const completed = this.menu.find((s) => s.substepId === currentSubstepId);
        const upcoming = this.menu.find((s) => s.substepId === next.substepId);
        if (completed && upcoming && completed.phaseKey !== upcoming.phaseKey) {
          runInAction(() => {
            this.phaseTransition = {
              completedPhaseKey: completed.phaseKey,
              completedPhaseName: completed.phaseName,
              next: { substepId: next.substepId, roomId: next.roomId },
            };
            this.position = null;
            this.options = [];
            this.packages = [];
          });
        } else {
          await this.goToSubstep({ substepId: next.substepId, roomId: next.roomId });
        }
      }
    } catch {
      runInAction(() => {
        this.isLoadingNext = false;
        this.error = 'Impossibile passare al passo successivo. Riprova.';
      });
    }
  }

  /** Chiude la schermata di transizione fase e naviga al primo substep della fase successiva. */
  async continueToNextPhase(): Promise<void> {
    const transition = this.phaseTransition;
    if (transition === null) return;
    runInAction(() => {
      this.phaseTransition = null;
    });
    await this.goToSubstep(transition.next);
  }

  async loadPackages(): Promise<void> {
    if (this.configurationId === null || this.position === null) return;
    this.isLoadingPackages = true;
    this.error = null;
    try {
      const { packages, copy } = await wizardApi.getPackages(
        this.configurationId,
        this.position.substepId,
        this.position.roomId,
      );
      runInAction(() => {
        this.packages = packages;
        this.substepCopy = copy ?? null;
        this.isLoadingPackages = false;
      });
    } catch {
      runInAction(() => {
        this.error = 'Impossibile caricare i pacchetti per questo passo. Riprova.';
        this.isLoadingPackages = false;
      });
    }
  }

  selectPackage(packageId: number): void {
    this.selectedPackageId = packageId;
    this.invalidationPreview = null;
  }

  /** Chiede al backend, in dryRun, cosa succederebbe confermando la selezione corrente. */
  async previewSelection(): Promise<SaveAnswerResponse | null> {
    if (
      this.configurationId === null ||
      this.position === null ||
      this.selectedPackageId === null
    ) {
      return null;
    }
    try {
      const result = await wizardApi.saveAnswer(
        this.configurationId,
        {
          substepId: this.position.substepId,
          packageId: this.selectedPackageId,
          ...(this.position.roomId != null ? { roomId: this.position.roomId } : {}),
        },
        true,
      );
      runInAction(() => {
        this.invalidationPreview = result;
      });
      return result;
    } catch {
      runInAction(() => {
        this.error = 'Impossibile verificare la selezione. Riprova.';
      });
      return null;
    }
  }

  /**
   * Conferma la selezione corrente: salva la risposta, poi chiede al backend
   * quale sia il prossimo substep (GET next) e vi si sposta. Ricarica il menu
   * per riflettere il nuovo stato isAnswered (ed eventuali invalidazioni a cascata).
   */
  async confirmSelection(): Promise<void> {
    if (
      this.configurationId === null ||
      this.position === null ||
      this.selectedPackageId === null
    ) {
      return;
    }
    const configurationId = this.configurationId;
    const currentSubstepId = this.position.substepId;
    const roomId = this.position.roomId;

    this.isSaving = true;
    this.error = null;
    try {
      await wizardApi.saveAnswer(configurationId, {
        substepId: currentSubstepId,
        packageId: this.selectedPackageId,
        ...(roomId != null ? { roomId } : {}),
      });

      runInAction(() => {
        this.isSaving = false;
        this.isLoadingNext = true;
      });

      const { next } = await wizardApi.getNext(configurationId, currentSubstepId, roomId);

      runInAction(() => {
        this.isLoadingNext = false;
        this.invalidationPreview = null;
      });

      // Ricarica il menu per riflettere isAnswered aggiornato ed eventuali invalidazioni.
      await this.loadMenu(configurationId);

      if (next === null) {
        runInAction(() => {
          this.isCompleted = true;
          this.position = null;
          this.packages = [];
          this.selectedPackageId = null;
        });
      } else {
        await this.goToSubstep({ substepId: next.substepId, roomId: next.roomId });
      }
    } catch {
      runInAction(() => {
        this.isSaving = false;
        this.isLoadingNext = false;
        this.error = 'Impossibile salvare la risposta. Riprova.';
      });
    }
  }

  /** Menu appiattito, ordinato per (phaseOrder, stepOrder, substepOrder). */
  get orderedMenu(): MenuSubstep[] {
    return [...this.menu].sort((a, b) => {
      if (a.phaseOrder !== b.phaseOrder) return a.phaseOrder - b.phaseOrder;
      if (a.stepOrder !== b.stepOrder) return a.stepOrder - b.stepOrder;
      return a.substepOrder - b.substepOrder;
    });
  }

  /**
   * Menu raggruppato per fase → stanza → step → substep, ordinato per gli
   * *Order del backend. Le fasi non room-major (roomId sempre null) hanno un
   * solo MenuRoomGroup con roomId/roomLabel null, che il SideMenu non mostra
   * come intestazione — per la fase BAGNO invece produce un gruppo per
   * ciascuna stanza (es. "Bagno 1", "Bagno 2"), come nel legacy.
   */
  get groupedMenu(): MenuPhaseGroup[] {
    const phaseMap = new Map<number, MenuPhaseGroup>();

    for (const s of this.menu) {
      let phase = phaseMap.get(s.phaseId);
      if (!phase) {
        phase = {
          phaseId: s.phaseId,
          phaseKey: s.phaseKey,
          phaseName: s.phaseName,
          phaseOrder: s.phaseOrder,
          rooms: [],
        };
        phaseMap.set(s.phaseId, phase);
      }

      const roomKey = s.roomId ?? 'none';
      let room = phase.rooms.find((r) => (r.roomId ?? 'none') === roomKey);
      if (!room) {
        room = { roomId: s.roomId, roomLabel: s.roomLabel, steps: [] };
        phase.rooms.push(room);
      }

      let step = room.steps.find((st) => st.stepId === s.stepId);
      if (!step) {
        step = {
          stepId: s.stepId,
          stepKey: s.stepKey,
          stepName: s.stepName,
          stepOrder: s.stepOrder,
          substeps: [],
        };
        room.steps.push(step);
      }
      step.substeps.push(s);
    }

    const phases = Array.from(phaseMap.values());
    phases.sort((a, b) => a.phaseOrder - b.phaseOrder);
    for (const phase of phases) {
      for (const room of phase.rooms) {
        room.steps.sort((a, b) => a.stepOrder - b.stepOrder);
        for (const step of room.steps) {
          step.substeps.sort((a, b) => a.substepOrder - b.substepOrder);
        }
      }
    }
    return phases;
  }

  get answeredCount(): number {
    return this.menu.filter((s) => s.isAnswered).length;
  }

  get totalCount(): number {
    return this.menu.length;
  }

  get completionPercentage(): number {
    if (this.totalCount === 0) return 0;
    return Math.round((this.answeredCount / this.totalCount) * 100);
  }

  get currentMenuEntry(): MenuSubstep | undefined {
    if (this.position === null) return undefined;
    const { substepId, roomId } = this.position;
    // Match esatto (room-major). Fallback su substepId con menu roomId null: i substep "room-inner"
    // (es. CARTA PARATI) hanno UNA voce template con roomId null; la stanza corrente arriva da /next
    // e vive in position.roomId, quindi va matchata a prescindere.
    return (
      this.menu.find((s) => s.substepId === substepId && s.roomId === roomId) ??
      this.menu.find((s) => s.substepId === substepId && s.roomId === null)
    );
  }

  get selectedPackage(): WizardPackage | undefined {
    return this.packages.find((p) => p.packageId === this.selectedPackageId);
  }

  /** inputType del substep corrente (deciso dal backend): 'options' | 'packages' | null. */
  get currentInputType(): 'options' | 'packages' | null {
    return this.currentMenuEntry?.inputType ?? null;
  }
}

export const wizardStore = new WizardStore();
